<script>
import { defineAsyncComponent, ref, computed } from 'vue';
import { mapGetters, useStore } from 'vuex';

import Sidebar from 'dashboard/components/layout/Sidebar.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components/widgets/FloatingCallWidget.vue')
);

const BrowserPhoneWidget = defineAsyncComponent(
  () =>
    import('dashboard/modules/browserPhone/components/BrowserPhoneWidget.vue')
);


import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useCallsStore } from 'dashboard/stores/calls';
import Functionbar from '../../components/layout/FunctionBar.vue';
import AccountSelector from 'dashboard/components/layout/sidebarComponents/AccountSelector.vue';

export default {
  components: {
    Sidebar,
    CommandBar,
    AddAccountModal,
    UpgradePage,
    FloatingCallWidget,
    BrowserPhoneWidget,
    MobileSidebarLauncher,
    Functionbar,
    AccountSelector,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();
    const vuexStore = useStore();

    return {
      uiSettings,
      updateUISettings,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
      canUseNativeBrowserPhone: computed(
        () => vuexStore.getters.getCurrentAccountHasBrowserPhoneAccess
      ),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      storeCurrentUser: 'getCurrentUser',
    }),
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    hasAccounts() {
      const { accounts = [] } = this.storeCurrentUser || {};
      return accounts.length > 0;
    },
    isSidebarOpen() {
      if (this.$route.name !== 'home') {
        return true;
      }
      const { show_secondary_sidebar: showSecondarySidebar } = this.uiSettings;
      return showSecondarySidebar;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'conversation_workflow_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    storeCurrentUser: {
      immediate: true,
      handler(user) {
        if (user && !this.hasAccounts) {
          this.showCreateAccountModal = true;
        }
      },
    },
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    emitter.on(BUS_EVENTS.TOGGLE_SIDEMENU, this.toggleSidebar);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    emitter.off(BUS_EVENTS.TOGGLE_SIDEMENU, this.toggleSidebar);
  },
  methods: {
    handleResize() {
      const { SMALL_SCREEN_BREAKPOINT, LAYOUT_TYPES } = wootConstants;
      let throttled = false;

      if (throttled) {
        return;
      }
      throttled = true;

      setTimeout(() => {
        throttled = false;
        if (window.innerWidth <= SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      }, 150);
    },
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleSidebar() {
      if (this.$route.name !== 'home') {
        return;
      }
      this.updateUISettings({
        show_secondary_sidebar: !this.isSidebarOpen,
        previously_used_sidebar_view: !this.isSidebarOpen,
      });
    },
    openNotificationPanel() {
      this.$router.push({ name: 'notifications_index' });
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col flex-grow overflow-hidden text-n-slate-12 min-h-0 h-full"
  >
    <header class="flex-shrink-0 w-full">
      <Functionbar />
    </header>
    <div class="flex flex-1 min-h-0 overflow-hidden relative">
      <BrowserPhoneWidget v-if="canUseNativeBrowserPhone" />
      <Sidebar
        :show-secondary-sidebar="isSidebarOpen"
        @toggle-account-modal="toggleAccountModal"
        @show-create-account-modal="openCreateAccountModal"
        @open-notification-panel="openNotificationPanel"
      />

      <main
        class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
      >
        <UpgradePage
          v-show="showUpgradePage"
          ref="upgradePageRef"
          :bypass-upgrade-page="bypassUpgradePage"
        >
          <MobileSidebarLauncher
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
        </UpgradePage>
        <template v-if="!showUpgradePage">
          <router-view />
          <CommandBar />
          <MobileSidebarLauncher
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
          <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
        </template>
        <AddAccountModal
          :show="showCreateAccountModal"
          :has-accounts="hasAccounts"
          @close-account-create-modal="closeCreateAccountModal"
        />
        <AccountSelector
          :show-account-modal="showAccountModal"
          @close-account-modal="toggleAccountModal"
          @show-create-account-modal="openCreateAccountModal"
        />
      </main>
    </div>
  </div>
</template>
